/**
 * Local-only registration wizard jump.
 *
 * Edit `REGISTRATION_DEV_JUMP` below, then reload `/register`.
 * Disabled automatically outside Vite DEV builds (`import.meta.env.DEV`).
 *
 * Seeds prior steps with valid fixture data so Back / Next / zod validation work.
 * Uses a fake ABP user id so returning to Account → Next does not call register.
 * Final CRM submit will fail with the fake id — use a real account path for end-to-end CRM tests.
 */
import {
  registrationExtendedDefaults,
  STAKEHOLDER_AFFECTED_PUBLIC,
  STAKEHOLDER_EMERGENCY_RESPONDER,
  type DispatchCapabilitiesPart,
  type RegistrationAccountValues,
  type RegistrationExtendedFormValues,
  type RegistrationStepId,
} from "@buxus/shared";
import type { RegistrationUiStep } from "./stepNavigation";

/** Stakeholder path used when seeding account + which profile steps appear. */
export type RegistrationDevJumpPreset = "emergencyResponder" | "commander" | "affectedPublic";

export type RegistrationDevJumpConfig = {
  /**
   * UI step to open on load.
   * Set to `null` to disable the jump (normal flow from Quick Code).
   */
  startStep: RegistrationUiStep | null;
  /** Which registration path to seed (affects visible steps after Account). */
  preset: RegistrationDevJumpPreset;
  /**
   * When `startStep` is `dispatchCapabilities`, which of the 3 parts to open (1–3).
   * Ignored for other steps. Defaults to 1.
   */
  dispatchPart?: DispatchCapabilitiesPart;
};

/**
 * Flip `startStep` to the screen under test, then reload `/register`.
 * Examples: `"agencyCapability"`, `"dispatchCapabilities"`, `"mailing"`, `"additionalQuestions"`.
 * Set `startStep: null` (current) for the normal Quick Code → … flow.
 *
 * Prefer `commander` (EC) when walking the full wizard — it includes agency
 * capability / procedure / training / mutual aid that plain ER does not.
 *
 * Only active in Vite DEV (`import.meta.env.DEV`); production builds ignore this.
 */
export const REGISTRATION_DEV_JUMP: RegistrationDevJumpConfig = {
  startStep: null,
  preset: "commander",
  dispatchPart: 3,
};

const DEV_JUMP_USER_ID = "00000000-dev0-jump-0000-000000000001";

const DEV_ACCOUNT_EMAIL = "reg-dev-jump@example.com";

export type RegistrationDevJumpBootstrap = {
  step: RegistrationUiStep;
  hasQuickCode: false;
  form: RegistrationAccountValues;
  extendedForm: RegistrationExtendedFormValues;
  abpUserId: string;
  committedAccountEmail: string;
  dispatchPart: DispatchCapabilitiesPart;
};

function accountFixture(preset: RegistrationDevJumpPreset): RegistrationAccountValues {
  if (preset === "affectedPublic") {
    return {
      stakeholderType: STAKEHOLDER_AFFECTED_PUBLIC,
      jobTitle: "",
      isUnaffiliatedProfessionalErTrainer: "",
      communicationsRole: "",
      firstName: "Dev",
      lastName: "Jump",
      phone: "(512) 555-0100",
      email: DEV_ACCOUNT_EMAIL,
      password: "DevJump1!",
      passwordConfirm: "DevJump1!",
    };
  }

  const jobTitle = preset === "commander" ? "Fire Chief" : "Volunteer Firefighter";
  return {
    stakeholderType: STAKEHOLDER_EMERGENCY_RESPONDER,
    jobTitle,
    isUnaffiliatedProfessionalErTrainer: "No",
    communicationsRole: "",
    firstName: "Dev",
    lastName: "Jump",
    phone: "(512) 555-0100",
    email: DEV_ACCOUNT_EMAIL,
    password: "DevJump1!",
    passwordConfirm: "DevJump1!",
  };
}

function seedMailing(ext: RegistrationExtendedFormValues): void {
  ext.mailingAddress1 = "100 Dev Jump St";
  ext.mailingAddress2 = "Suite 1";
  ext.mailingCity = "Austin";
  ext.mailingCountry = "United States";
  ext.mailingState = "Texas";
  ext.mailingCounty = "Travis";
  ext.mailingZip = "78701";
}

function seedJurisdictions(ext: RegistrationExtendedFormValues): void {
  ext.jurisdictions = [{ country: "United States", state: "Texas", county: "Travis" }];
}

function seedOrganization(ext: RegistrationExtendedFormValues): void {
  ext.institution = "Dev Jump Fire Dept";
  ext.organizationType = "";
  ext.department = "";
  ext.institutionPhone = "(512) 555-0199";
}

function seedAgencyInfo(ext: RegistrationExtendedFormValues): void {
  // Include a dispatch agency type so Dispatch Capabilities stays on the visible path.
  ext.typeOfAgency = ["Fire", "Primary PSAP"];
  ext.stationNameOrNumber = "Station 1";
  ext.nameOfAgencyCommandingOfficer = "Chief Dev";
  ext.nonEmergencyNumberForAgencyOrDepartment = "(512) 555-0188";
  ext.website = "https://example.com";
  ext.staffed = "Volunteer";
  ext.numberOfPeopleInDepartment = "12";
  ext.dispatchRadioSystemsUsedByAgency = ["VHF - Analog"];
  ext.dispatchRadioSystemsUsedByAgencyOther = "";
  ext.dispatchIsRadioSystemP25Compliant = false;
  ext.dispatchIsRadioSystemEncrypted = true;
}

function seedAgencyCapability(ext: RegistrationExtendedFormValues): void {
  ext.canYourDepartmentProvide = ["Foam capabilities", "Medical assistance"];
  // Retired capability Yes/Nos — leave false; options now live on canYourDepartmentProvide.
  ext.provideMedicalAssistance = false;
  ext.assistInDirectingTraffic = false;
  ext.radioCommunication = false;
  ext.trunkRadios = false;
  ext.computerInternetAccess = false;
}

function seedAgencyProcedure(ext: RegistrationExtendedFormValues): void {
  ext.naturalGasEmergencies = true;
  ext.hazardousLiquidContingencyPlan = false;
  ext.publicEvacuation = true;
}

function seedAgencyTraining(ext: RegistrationExtendedFormValues): void {
  ext.hazwoper = "Awareness";
  ext.naturalGasNfpa329 = true;
  ext.hazardousMaterials = false;
  ext.pipelineEmergencies = true;
}

function seedMutualAid(ext: RegistrationExtendedFormValues): void {
  ext.mutualAidAgreements = true;
  ext.withWhom = "Neighboring county";
}

function seedAdditionalQuestions(ext: RegistrationExtendedFormValues): void {
  ext.howDidYouFindOutAboutBuxus = "Email";
  ext.howDidYouFindOutAboutBuxusAgency = "";
  ext.howDidYouFindOutAboutBuxusAssociation = "";
  ext.howDidYouFindOutAboutBuxusConference = "";
  ext.howDidYouFindOutAboutBuxusOperator = "";
  ext.howDidYouFindOutAboutBuxusTrainer = "";
  ext.howDidYouFindOutAboutBuxusWebinar = "";
}

function seedDispatchPart1(ext: RegistrationExtendedFormValues): void {
  ext.dispatchFacilityClassification = "Primary";
  ext.dispatchDisciplinesServedByAgency = ["Fire - Municipal"];
}

function seedDispatchPart2(ext: RegistrationExtendedFormValues): void {
  ext.dispatchMainAreaOfResponsibility = "Urban";
  ext.dispatchEstPopulationOfResponsibilityArea = "50000";
  ext.dispatchComputerAidedDispatchSystemUsed = "Motorola";
  ext.dispatchComputerAidedDispatchSystemUsedOther = "";
  ext.dispatchCanDetermineCallerLatLongGis = true;
  ext.dispatchProgramsUsedByAgency = ["Buxus"];
  ext.dispatchPublicNotificationSystemUsed = "CodeRED";
  ext.dispatchPublicNotificationSystemUsedOther = "";
}

function seedDispatchPart3(ext: RegistrationExtendedFormValues): void {
  ext.dispatchDoesStateRequireCertification = "All";
  ext.dispatchDoesStateRequireCertificationOther = "";
  ext.dispatchIsContinuingEducationRequired = "Yes";
  ext.dispatchTrainingCoversEmergencyTopics = ["Pipelines", "Natural Gas"];
  ext.dispatchHasAccessToEmergencyTopics = ["Natural Gas"];
  ext.dispatchSopSogForEmergencyTopics = ["Pipeline emergencies"];
  ext.dispatchCompletedCoursesOnEmergencyTopics = ["Pipeline preparedness and response"];
}

/** Seed fields required to leave `engineStep` as if the user completed it. */
function seedCompletedEngineStep(
  engineStep: RegistrationStepId,
  ext: RegistrationExtendedFormValues,
): void {
  switch (engineStep) {
    case "quickCode":
    case "account":
      break;
    case "mailing":
      seedMailing(ext);
      break;
    case "jurisdictions":
      seedJurisdictions(ext);
      break;
    case "organization":
      seedOrganization(ext);
      break;
    case "agencyInfo":
      seedAgencyInfo(ext);
      break;
    case "agencyCapability":
      seedAgencyCapability(ext);
      break;
    case "agencyProcedure":
      seedAgencyProcedure(ext);
      break;
    case "agencyTraining":
      seedAgencyTraining(ext);
      break;
    case "dispatchCapabilities":
      seedDispatchPart1(ext);
      seedDispatchPart2(ext);
      seedDispatchPart3(ext);
      break;
    case "agencyAgreement":
      seedMutualAid(ext);
      break;
    case "additionalQuestions":
      seedAdditionalQuestions(ext);
      break;
    default:
      break;
  }
}

function engineIdForUiStart(startStep: RegistrationUiStep): RegistrationStepId | null {
  if (startStep === "accountBasics") return "account";
  if (
    startStep === "quickCode" ||
    startStep === "quickCodeEntry" ||
    startStep === "expressReturnQuickCode" ||
    startStep === "quickCodeAlreadyRegistered" ||
    startStep === "done"
  ) {
    return null;
  }
  return startStep as RegistrationStepId;
}

/** Global profile order used only to decide which fixtures to fill before the jump target. */
const SEED_ORDER: RegistrationStepId[] = [
  "account",
  "mailing",
  "jurisdictions",
  "organization",
  "agencyInfo",
  "agencyCapability",
  "agencyProcedure",
  "agencyTraining",
  "dispatchCapabilities",
  "agencyAgreement",
  "additionalQuestions",
];

/**
 * Returns bootstrap state when the jump is active, otherwise `null`.
 * Call once when initializing RegistrationFlow state.
 */
export function getRegistrationDevJumpBootstrap(
  config: RegistrationDevJumpConfig = REGISTRATION_DEV_JUMP,
): RegistrationDevJumpBootstrap | null {
  if (!import.meta.env.DEV || !config.startStep) return null;

  const form = accountFixture(config.preset);
  const extendedForm = registrationExtendedDefaults();
  const targetEngine = engineIdForUiStart(config.startStep);
  const dispatchPart: DispatchCapabilitiesPart =
    config.startStep === "dispatchCapabilities" ? (config.dispatchPart ?? 1) : 1;

  if (targetEngine) {
    for (const id of SEED_ORDER) {
      if (id === targetEngine) break;
      seedCompletedEngineStep(id, extendedForm);
    }
  }

  // When landing mid-dispatch, seed earlier parts so Back / revalidation stay valid.
  if (config.startStep === "dispatchCapabilities") {
    if (dispatchPart > 1) seedDispatchPart1(extendedForm);
    if (dispatchPart > 2) seedDispatchPart2(extendedForm);
  }

  console.info(
    `[registrationDevJump] Starting at "${config.startStep}"` +
      (config.startStep === "dispatchCapabilities" ? ` part ${dispatchPart}` : "") +
      ` (preset=${config.preset}). Set REGISTRATION_DEV_JUMP.startStep = null to disable.`,
  );

  return {
    step: config.startStep,
    hasQuickCode: false,
    form,
    extendedForm,
    abpUserId: DEV_JUMP_USER_ID,
    committedAccountEmail: DEV_ACCOUNT_EMAIL,
    dispatchPart,
  };
}
